import { Box } from '@mui/system';
import {
  BarElement,
  CategoryScale,
  ChartData,
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  Tooltip,
} from 'chart.js';
import { format } from 'date-fns';
import { ChartSeriesEntry } from 'Portfolio';
import React from 'react';
import { Bar } from 'react-chartjs-2';
import ExtendedLiquid from '../services/ExtendedLiquid';
import { Palette } from 'Palette';
import { getColorForLabel, StyledTypography } from './styles/styles';
import { drawRightBorderPlugin } from './Util/ChartExtensions';

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend, Filler);

interface MultiIRRBarChartProps {
  data: Record<string, any>[]; // { month: string; [investmentLabel: string]: number }
  seriesKeys: string[];
  tone: keyof typeof Palette;
}

export default function MultiIRRBarChart({ data, seriesKeys }: MultiIRRBarChartProps) {
  const labels = data.map((d) => format(new Date(`${d.month}-01`), 'MMM yy'));

  const chartData: ChartData<'bar'> = {
    labels,
    datasets: seriesKeys.map((key, index) => ({
      label: key,
      data: data.map((d) => d[key]?.value ?? 0),
      backgroundColor: (ctx) => {
        const i = ctx.dataIndex;
        const entry = data[i][key] as ChartSeriesEntry | undefined;
        return entry?.type === 'actual' ? getColorForLabel(index) : 'transparent';
      },
      borderWidth: (ctx) => {
        const i = ctx.dataIndex;
        const entry = data[i][key] as ChartSeriesEntry | undefined;
        return entry?.type === 'forecast' ? 2 : 0;
      },
      borderDash: (ctx: any) => {
        const i = ctx.dataIndex;
        const entry = data[i][key] as ChartSeriesEntry | undefined;
        return entry?.type === 'forecast' ? [4, 2] : undefined;
      },
      borderColor: (ctx) => {
        const i = ctx.dataIndex;
        const entry = data[i][key] as ChartSeriesEntry | undefined;
        return entry?.type === 'forecast' ? getColorForLabel(index) : undefined;
      },
    })),
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: function (context: any) {
            const value = context.raw;
            const label = context.dataset.label || '';
            const formatted = ExtendedLiquid.currencyMillions(value, true, true, true, true); // 👈 your custom formatting
            return `${label}: ${formatted}`;
          },
        },
      },
    },
    interaction: {
      mode: 'index' as const,
      intersect: false,
    },
    scales: {
      x: {
        stacked: true,
        ticks: {
          maxRotation: 45,
        },
      },
      y: {
        beginAtZero: true,
        stacked: true,
        ticks: {
          callback: function (tickValue: string | number) {
            const value = typeof tickValue === 'string' ? parseFloat(tickValue) : tickValue;
            return ExtendedLiquid.currencyMillions(value, true, true, true);
          },
        },
      },
    },
  };

  return (
    <>
      <div style={{ width: '100%', height: 500 }} className="react-MultiIRRBarChart">
        <Bar data={chartData} options={options} plugins={[drawRightBorderPlugin]} />
      </div>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        flexWrap="wrap"
        gap={2}
        mt={1}
      >
        {seriesKeys.map((key, i) => (
          <Box key={key} display="flex" alignItems="center" gap={1}>
            <Box
              sx={{
                width: 24,
                height: 8,
                backgroundColor: getColorForLabel(i),
              }}
            />
            <StyledTypography tone="tone1" component="span">
              {key}
            </StyledTypography>
          </Box>
        ))}
      </Box>
    </>
  );
}
